import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from './ui/card';
import { BookOpen, ArrowLeft, Shield, Eye, Lock, Database } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center text-blue-600 hover:text-blue-700">
              <img 
                src="https://customer-assets.emergentagent.com/job_bookcritique-1/artifacts/inexx1ig_IMG-20250713-WA0004%281%29.jpg"
                alt="Book Review Specialists Logo"
                className="w-6 h-6 rounded-full object-cover mr-2"
              />
              <span className="font-semibold">Book Review Specialists</span>
            </Link>
            <Link to="/" className="btn btn-secondary">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Shield className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <h1 className="text-4xl font-bold text-slate-800 mb-4">Privacy Policy</h1>
            <p className="text-lg text-slate-600">
              Your privacy is important to us. This policy explains how we collect, use, and protect your information.
            </p>
            <p className="text-sm text-slate-500 mt-2">
              Last updated: January 2025
            </p>
          </div>

          <div className="space-y-8">
            <Card className="card">
              <div className="flex items-start mb-4">
                <Eye className="w-6 h-6 text-blue-600 mt-1 mr-3" />
                <div>
                  <h2 className="text-2xl font-semibold text-slate-800 mb-4">Information We Collect</h2>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">Personal Information</h3>
                  <p className="text-slate-600 mb-2">When you enroll in our courses, we collect:</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                    <li>Full name and email address</li>
                    <li>Phone number (optional)</li>
                    <li>Payment information (processed securely via Square)</li>
                    <li>Course preferences and selections</li>
                    <li>Communication preferences</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">Automatically Collected Information</h3>
                  <p className="text-slate-600 mb-2">We automatically collect certain information when you visit our website:</p>
                  <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                    <li>IP address and browser information</li>
                    <li>Pages visited and time spent on our site</li>
                    <li>Referral source and search terms used</li>
                    <li>Device and operating system information</li>
                  </ul>
                </div>
              </div>
            </Card>

            <Card className="card">
              <div className="flex items-start mb-4">
                <Database className="w-6 h-6 text-green-600 mt-1 mr-3" />
                <div>
                  <h2 className="text-2xl font-semibold text-slate-800 mb-4">How We Use Your Information</h2>
                </div>
              </div>
              
              <div className="space-y-4">
                <p className="text-slate-600">We use your information to:</p>
                <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                  <li><strong>Provide our services:</strong> Process enrollments, deliver course content, and manage your account</li>
                  <li><strong>Communication:</strong> Send course updates, important announcements, and support messages</li>
                  <li><strong>Payment processing:</strong> Handle transactions securely through Square</li>
                  <li><strong>Improve our services:</strong> Analyze usage patterns to enhance our courses and website</li>
                  <li><strong>Marketing:</strong> Send promotional materials about new courses and features (with your consent)</li>
                  <li><strong>Legal compliance:</strong> Meet regulatory requirements and protect against fraud</li>
                </ul>
              </div>
            </Card>

            <Card className="card">
              <div className="flex items-start mb-4">
                <Lock className="w-6 h-6 text-purple-600 mt-1 mr-3" />
                <div>
                  <h2 className="text-2xl font-semibold text-slate-800 mb-4">Information Sharing and Disclosure</h2>
                </div>
              </div>
              
              <div className="space-y-4">
                <p className="text-slate-600">We do not sell, trade, or rent your personal information. We may share your information only in these circumstances:</p>
                <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                  <li><strong>Service providers:</strong> With trusted third parties who help us operate our business (e.g., Square for payments, email services)</li>
                  <li><strong>Legal requirements:</strong> When required by law or to protect our rights and safety</li>
                  <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                  <li><strong>With your consent:</strong> When you explicitly agree to share information with third parties</li>
                </ul>
              </div>
            </Card>

            <Card className="card">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">Data Security</h2>
              <p className="text-slate-600 mb-4">
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-1 ml-4">
                <li>SSL encryption for data transmission</li>
                <li>Secure servers and databases</li>
                <li>Regular security audits and updates</li>
                <li>Access controls and employee training</li>
                <li>Secure payment processing via Square</li>
              </ul>
            </Card>

            <Card className="card">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">Your Rights</h2>
              <p className="text-slate-600 mb-4">You have the following rights regarding your personal information:</p>
              <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong>Correction:</strong> Ask us to correct any inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal requirements)</li>
                <li><strong>Portability:</strong> Request a copy of your data in a structured, machine-readable format</li>
                <li><strong>Objection:</strong> Object to processing of your personal information for marketing purposes</li>
                <li><strong>Withdrawal:</strong> Withdraw consent at any time where processing is based on consent</li>
              </ul>
            </Card>

            <Card className="card">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">Cookies and Tracking</h2>
              <p className="text-slate-600 mb-4">
                We use cookies and similar technologies to improve your experience on our website. You can control cookies through your browser settings.
              </p>
              <div className="space-y-2">
                <p className="text-slate-600"><strong>Essential cookies:</strong> Required for basic website functionality</p>
                <p className="text-slate-600"><strong>Analytics cookies:</strong> Help us understand how visitors use our site</p>
                <p className="text-slate-600"><strong>Marketing cookies:</strong> Used to deliver relevant advertisements</p>
              </div>
            </Card>

            <Card className="card">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">Children's Privacy</h2>
              <p className="text-slate-600">
                Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children under 18. If you are a parent or guardian and believe your child has provided us with personal information, please contact us.
              </p>
            </Card>

            <Card className="card">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">International Transfers</h2>
              <p className="text-slate-600">
                Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your information in accordance with applicable data protection laws.
              </p>
            </Card>

            <Card className="card">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">Changes to This Policy</h2>
              <p className="text-slate-600">
                We may update this privacy policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date. We encourage you to review this policy periodically.
              </p>
            </Card>

            <Card className="card bg-blue-50 border-blue-200">
              <h2 className="text-2xl font-semibold text-slate-800 mb-4">Contact Us</h2>
              <p className="text-slate-600 mb-4">
                If you have any questions about this privacy policy or our data practices, please contact us:
              </p>
              <div className="space-y-2 text-slate-600">
                <p><strong>Email:</strong> info@virtualautochat.uk</p>
                <p><strong>Website:</strong> bookreviewspecialists.app</p>
                <p><strong>Address:</strong> Book Review Specialists, United Kingdom</p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;